import { Direction } from "@/game/entities/Familiar";
import { MAP_SIZE } from "@/game/map/TileMap";
import { ThemeName } from "@/game/map/ThemeConfig";

/**
 * ステージバリアント（階層ダンジョンの1フロア分のデータ）
 *
 * 階層ダンジョンでは複数のフロアを同じ魔導書（プログラム）でクリアする。
 * 各フロアのゴーレム配置・宝位置・壁配置はバリアントで定義する。
 */
export interface StageVariant {
  familiar: {
    x: number;
    y: number;
    direction: Direction;
  };
  treasure: {
    x: number;
    y: number;
  };
  walls?: { x: number; y: number }[];
}

/**
 * ステージデータの型
 *
 * variants がある場合は階層ダンジョン。
 * 全フロアを同じプログラムでクリアする必要がある。
 * variants がない場合は単一フロアの通常ステージ。
 */
export interface StageData {
  id: number;
  name: string;
  description: string;
  mp: number;
  familiar: {
    x: number;
    y: number;
    direction: Direction;
  };
  treasure: {
    x: number;
    y: number;
  };
  walls?: { x: number; y: number }[];
  variants?: StageVariant[];
  /** サブブック定義スロット数（0 or undefined = サブブック非対応） */
  subbookSlots?: number;
  /** テーマ名（未指定時は 'cave'） */
  theme?: ThemeName;
}

/**
 * 一本道の壁座標を生成（13x13マップ用）
 *
 * openTilesに含まれないタイルをすべて壁にする。
 * チュートリアルステージで余計な解法を防ぐために使用。
 */
export function createCorridorWalls(
  openTiles: { x: number; y: number }[]
): { x: number; y: number }[] {
  const openSet = new Set(openTiles.map((t) => `${t.x},${t.y}`));
  const walls: { x: number; y: number }[] = [];
  for (let y = 0; y < MAP_SIZE; y++) {
    for (let x = 0; x < MAP_SIZE; x++) {
      if (!openSet.has(`${x},${y}`)) {
        walls.push({ x, y });
      }
    }
  }
  return walls;
}

/**
 * ステージ一覧
 */
export const STAGES: StageData[] = [
  // ステージ1: 直線の一本道。前進のみでクリア。
  // 解法: 4マス前進(8MP) ≤ 10MP
  {
    id: 1,
    name: "はじめの一歩",
    description: "まっすぐ進んで宝を取ろう",
    mp: 10,
    familiar: { x: 6, y: 1, direction: "down" },
    treasure: { x: 6, y: 5 },
    walls: createCorridorWalls([
      { x: 6, y: 1 }, // スタート
      { x: 6, y: 2 },
      { x: 6, y: 3 },
      { x: 6, y: 4 },
      { x: 6, y: 5 }, // 宝
    ]),
  },
  // ステージ2: L字の一本道。前進＋方向転換を学ぶ。
  // 解法: 4マス前進(8) + 左向く(1) + 2マス前進(4) = 13MP ≤ 15MP
  {
    id: 2,
    name: "曲がり角",
    description: "曲がり角の先に宝がある",
    mp: 15,
    familiar: { x: 8, y: 3, direction: "left" },
    treasure: { x: 4, y: 5 },
    walls: createCorridorWalls([
      { x: 8, y: 3 }, // スタート
      { x: 7, y: 3 },
      { x: 6, y: 3 },
      { x: 5, y: 3 },
      { x: 4, y: 3 }, // 角
      { x: 4, y: 4 },
      { x: 4, y: 5 }, // 宝
    ]),
  },
  // ステージ3: 直線の一本道。変数（オーブ）を使って前進コストを削減。
  // 直接4マス前進 = コスト8 > MP7 → 足す + 変数前進が必要
  // 解法例: 緑に4足す(4) + 緑マス前進(2) = 6MP ≤ 7MP
  {
    id: 3,
    name: "オーブの力",
    description: "オーブに数を入れて進もう",
    mp: 7,
    familiar: { x: 6, y: 1, direction: "down" },
    treasure: { x: 6, y: 5 },
    walls: createCorridorWalls([
      { x: 6, y: 1 }, // スタート
      { x: 6, y: 2 },
      { x: 6, y: 3 },
      { x: 6, y: 4 },
      { x: 6, y: 5 }, // 宝
    ]),
  },
  // ステージ4: L字の一本道。かけ算で大きな数を作って効率的に移動。
  // 直接: 4マス前進(8) × 2 + 左向く(1) = 17 > MP15
  // 変数: 緑に2足す(2) + 緑に2かける(2) + 緑マス前進(2) + 左向く(1) + 緑マス前進(2) = 9MP
  // シンプル解: 緑に4足す(4) + 緑マス前進(2) + 左向く(1) + 緑マス前進(2) = 9MP
  {
    id: 4,
    name: "かけ算の力",
    description: "かけ算で大きな数を作ろう",
    mp: 15,
    familiar: { x: 8, y: 1, direction: "left" },
    treasure: { x: 4, y: 5 },
    walls: createCorridorWalls([
      { x: 8, y: 1 }, // スタート
      { x: 7, y: 1 },
      { x: 6, y: 1 },
      { x: 5, y: 1 },
      { x: 4, y: 1 }, // 角
      { x: 4, y: 2 },
      { x: 4, y: 3 },
      { x: 4, y: 4 },
      { x: 4, y: 5 }, // 宝
    ]),
  },
  // ステージ5: 2フロアの階層ダンジョン。条件分岐（if）を学ぶ。
  // 1階: 前方に壁 → 右に迂回、2階: 壁なし → 直進
  // 解法: if(壁あり){右向く, 1マス前進, 左向く} + 2マス前進 = 1+1+2+1+4 = 9MP ≤ 10MP
  ...((): StageData[] => {
    const floor1Open = [
      { x: 6, y: 3 }, // スタート
      { x: 5, y: 3 }, // 右迂回路（下向き時の右=左方向）
      { x: 5, y: 4 },
      { x: 5, y: 5 }, // 宝（右迂回先）
    ];
    const floor2Open = [
      { x: 6, y: 3 }, // スタート
      { x: 6, y: 4 }, // 直進
      { x: 6, y: 5 }, // 宝（直進先）
    ];
    const floor1Walls = createCorridorWalls(floor1Open);
    const floor2Walls = createCorridorWalls(floor2Open);
    return [
      {
        id: 5,
        name: "はじめての階層ダンジョン",
        description: "同じ魔導書で2つの階をクリアしよう",
        mp: 10,
        // 初期表示は1階のデータ
        familiar: { x: 6, y: 3, direction: "down" },
        treasure: { x: 5, y: 5 },
        walls: floor1Walls,
        variants: [
          {
            // 1階: 前方(6,4)は壁 → if(壁あり)が真 → 右迂回
            familiar: { x: 6, y: 3, direction: "down" },
            treasure: { x: 5, y: 5 },
            walls: floor1Walls,
          },
          {
            // 2階: 前方(6,4)は通行可能 → if(壁あり)が偽 → 直進
            familiar: { x: 6, y: 3, direction: "down" },
            treasure: { x: 6, y: 5 },
            walls: floor2Walls,
          },
        ],
      },
    ];
  })(),
  // ステージ6: 一本道の通行可能タイル定義
  // 1階: 右迂回ルート（前方(6,3)は壁 → 条件が真）
  // 2階: 左迂回ルート（前方(6,3)は通行可能 → 条件が偽）
  ...((): StageData[] => {
    const floor1Open = [
      { x: 6, y: 2 }, // スタート
      { x: 5, y: 2 }, // 右迂回路（下向き時の右=左方向）
      { x: 5, y: 3 },
      { x: 5, y: 4 },
      { x: 5, y: 5 }, // 宝（右迂回先）
    ];
    const floor2Open = [
      { x: 6, y: 2 }, // スタート
      { x: 6, y: 3 }, // 前方（壁なし → 条件が偽になる）
      { x: 7, y: 2 }, // 左迂回路（下向き時の左=右方向）
      { x: 7, y: 3 },
      { x: 7, y: 4 },
      { x: 7, y: 5 }, // 宝（左迂回先）
    ];
    const floor1Walls = createCorridorWalls(floor1Open);
    const floor2Walls = createCorridorWalls(floor2Open);
    return [
      {
        id: 6,
        name: "分かれ道の洞窟",
        description: "壁があるときとないとき、それぞれに備えよう",
        mp: 16,
        // 初期表示は1階のデータ
        familiar: { x: 6, y: 2, direction: "down" },
        treasure: { x: 5, y: 5 },
        walls: floor1Walls,
        // 解法: if(壁あり){右向く,1マス前進,左向く} else{左向く,1マス前進,右向く} + 3マス前進
        // = 1+1+2+1 + 1+1+2+1 + 6 = 16MP = MP上限
        // 一本道のため、ifの後にカードを追加する回避策が使えない
        variants: [
          {
            familiar: { x: 6, y: 2, direction: "down" },
            treasure: { x: 5, y: 5 },
            walls: floor1Walls,
          },
          {
            familiar: { x: 6, y: 2, direction: "down" },
            treasure: { x: 7, y: 5 },
            walls: floor2Walls,
          },
        ],
      },
    ];
  })(),
  // ステージ7: 繰り返しの回廊
  // 一本道4マスで宝まで直進。while を使えば少ないMPでクリアできる。
  // 解法: while(壁なし){1マス前進} = WHILE_BASE_COST(2) + FORWARD(2) = 4MP ≤ 5MP
  // 直接: forward(4) = 8MP > 5MP → 不可
  {
    id: 7,
    name: "繰り返しの回廊",
    description: "同じ動きを繰り返して宝を目指そう",
    mp: 5,
    familiar: { x: 6, y: 1, direction: "down" },
    treasure: { x: 6, y: 5 },
    walls: createCorridorWalls([
      { x: 6, y: 1 },
      { x: 6, y: 2 },
      { x: 6, y: 3 },
      { x: 6, y: 4 },
      { x: 6, y: 5 },
    ]),
  },
  // ステージ8: はじめてのサブブック（サブブック導入チュートリアル）
  // 2段階段の回廊。パターン[F1,TR,F1,TL]を2回繰り返して宝に到達する。
  //
  // マップ（13x13、通行可能タイル）:
  //   (8,1) → (8,2) → (7,2) → (7,3) → (6,3) → (6,4)
  //
  // インライン: (F1,TR,F1,TL) × 2 + F1 = (2+1+2+1)×2 + 2 = 14MP > 12 → 不可
  // サブブック解法: サブブック[F1,TR,F1,TL]=6MP + main[call,call,F1]=2+2+2=6MP → 計12MP ≤ 12MP
  {
    id: 8,
    name: "はじめてのサブブック",
    description: "同じ動きをサブブックにまとめてみよう",
    mp: 12,
    subbookSlots: 1,
    familiar: { x: 8, y: 1, direction: "down" },
    treasure: { x: 6, y: 4 },
    walls: createCorridorWalls([
      { x: 8, y: 1 }, // スタート
      { x: 8, y: 2 }, // 下へ1マス
      { x: 7, y: 2 }, // 左へ1マス
      { x: 7, y: 3 }, // 下へ1マス（2段目の入口）
      { x: 6, y: 3 }, // 左へ1マス
      { x: 6, y: 4 }, // 下へ1マス（宝）
    ]),
  },
  // ステージ9: 大きな呪文をまとめよう（サブブック応用）
  // 太い2段階段。パターン[F2,TR,F2,TL]を2回繰り返して宝に到達する。
  // ボディが大きい（10MP）ため、サブブックの効果がより明確。
  //
  // マップ（13x13、通行可能タイル）:
  //   (8,1) → (8,2) → (8,3) → (7,3) → (6,3) → (6,4) → (6,5) → (5,5) → (4,5) → (4,6)
  //
  // インライン: (F2,TR,F2,TL) × 2 + F1 = (4+1+4+1)×2 + 2 = 22MP > 18 → 不可
  // サブブック解法: サブブック[F2,TR,F2,TL]=10MP + main[call,call,F1]=2+2+2=6MP → 計16MP ≤ 18MP
  // ボーナス解法: while(壁なし){call} = 2+2+10 = 14MP
  {
    id: 9,
    name: "大きな呪文をまとめよう",
    description: "長い動きもサブブックにまとめられる",
    mp: 18,
    subbookSlots: 1,
    familiar: { x: 8, y: 1, direction: "down" },
    treasure: { x: 4, y: 6 },
    walls: createCorridorWalls([
      { x: 8, y: 1 }, // スタート
      { x: 8, y: 2 }, // 下へ1マス
      { x: 8, y: 3 }, // 下へ1マス（F2の到達点）
      { x: 7, y: 3 }, // 左へ1マス
      { x: 6, y: 3 }, // 左へ1マス（F2の到達点）
      { x: 6, y: 4 }, // 下へ1マス（2段目の入口）
      { x: 6, y: 5 }, // 下へ1マス（F2の到達点）
      { x: 5, y: 5 }, // 左へ1マス
      { x: 4, y: 5 }, // 左へ1マス（F2の到達点）
      { x: 4, y: 6 }, // 下へ1マス（宝）
    ]),
  },
  // ステージ10: 魔法書の力（サブブック発展）
  // 3段の均一階段。パターン[F1,TR,F1,TL]を3回繰り返し + F1で宝に到達する。
  //
  // マップ（13x13、通行可能タイル）:
  //   (8,1) → (8,2) → (7,2) → (7,3) → (6,3) → (6,4) → (5,4) → (5,5)
  //
  // インライン: (F1,TR,F1,TL) × 3 + F1 = (2+1+2+1)×3 + 2 = 20MP → MP不足（MP=18）
  // サブブック解法: サブブック[F1,TR,F1,TL]=6MP + main[call,call,call,F1]=2+2+2+2=8MP → 計14MP ≤ 18MP
  {
    id: 10,
    name: "魔法書の力",
    description: "よく使う動きをサブブックにまとめよう",
    mp: 18,
    subbookSlots: 3,
    familiar: { x: 8, y: 1, direction: "down" },
    treasure: { x: 5, y: 5 },
    walls: createCorridorWalls([
      { x: 8, y: 1 }, // スタート
      { x: 8, y: 2 }, // call 1: F1 下
      { x: 7, y: 2 }, // call 1: TR→F1 左
      { x: 7, y: 3 }, // call 2: F1 下
      { x: 6, y: 3 }, // call 2: TR→F1 左
      { x: 6, y: 4 }, // call 3: F1 下
      { x: 5, y: 4 }, // call 3: TR→F1 左
      { x: 5, y: 5 }, // main: F1 下（宝）
    ]),
  },
];

/**
 * ステージIDからステージデータを取得
 */
export function getStageById(id: number): StageData | undefined {
  return STAGES.find((stage) => stage.id === id);
}
